import { useLayoutEffect, useState, RefObject } from 'react';

interface UseTruncatedElementProps {
  ref: RefObject<HTMLElement>;
  content?: string;
  minLength?: number;
}

const useTruncatedElement = ({ ref, content, minLength }: UseTruncatedElementProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [isReadingMore, setIsReadingMore] = useState(false);

  useLayoutEffect(() => {
    const checkTruncation = () => {
      if (!ref.current) return;

      const { offsetHeight, scrollHeight } = ref.current;

      // Check via content length
      if (minLength) {
        const currentLength = ref.current.textContent?.length || 0;
        setIsTruncated(currentLength > minLength);
        return;
      }

      // Check via offset height
      setIsTruncated(offsetHeight < scrollHeight);
    };

    // Ensure content is rendered before checking
    const timeoutId = setTimeout(checkTruncation, 100);

    return () => clearTimeout(timeoutId);
  }, [ref, content, minLength]);

  const toggleIsShowingMore = () => {
    if (isReadingMore && ref.current) {
      ref.current.scrollTo({ top: 0 });
    }
    setIsReadingMore((prev) => !prev);
  };

  return {
    isTruncated,
    isReadingMore,
    toggleIsShowingMore
  };
};

export default useTruncatedElement;
